import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useDresser } from '../../../../../contexts/DrawerContext';
import useFileMatchesForRevision from '../../../../../hooks/api/revisions/useFileMatchesForRevision';
import useOnMount from '../../../../../hooks/useOnMount';
import { ResizableFileTreeLayout } from '../../../../generic/ResizableFileTreeLayout/ResizableFileTreeLayout';

import MatchFileTree from './MatchFileTree';
import MatchPathContent from './MatchPathContent';

import styles from './MatchesTab.module.less';

type Props = {
  revisionId: string;
  licenseId: string;
};

const MatchesTab: VoidFunctionComponent<Props> = ({ revisionId, licenseId }) => {
  const { setWidthPercent } = useDresser();
  useOnMount(() => setWidthPercent(100), () => setWidthPercent(undefined));

  const {
    isLoading,
    data,
  } = useFileMatchesForRevision(revisionId, licenseId);

  const [selectedMatch, setSelectedMatch] = useState<string | null>(null);

  const match = useMemo(
    () => {
      const path = selectedMatch?.startsWith('/') ? selectedMatch.slice(1) : selectedMatch;
      return data?.matches.find((m) => m.path === path);
    },
    [data?.matches, selectedMatch],
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.envelope}>
      <ResizableFileTreeLayout
        treeHeader="Files"
        contentHeader="Content"
        defaultTreeWidth={240}
        renderTree={({ width, height }) => (
          <MatchFileTree
            matches={data?.matches || []}
            selectedMatch={selectedMatch}
            setSelectedMatch={setSelectedMatch}
            width={width}
            height={height}
          />
        )}
      >
        {match && <MatchPathContent revisionId={revisionId} match={match} />}
      </ResizableFileTreeLayout>
    </div>
  );
};

export default MatchesTab;
